import React, { useState, useEffect, useCallback, useRef } from 'react'
import { AppStateProvider, useAppState } from './context/AppState'
import { GoalsInSight } from './components/GoalsInSight'
import { DayView } from './components/DayView'
import { WEEKDAYS } from './data/seed'
import { isWearOSDevice, isTinyWatchScreen, getDeviceDisplayName, getDeviceIcon, getDeviceDebugInfo } from './utils/deviceDetect'
import { WelcomePopup } from './components/WelcomePopup'
import './App.css'

const STORAGE_KEY = 'grim-productivity-v1'
const THEME_STORAGE_KEY = 'grim-theme'
const TINY_WATCH_MEDIA = '(max-width: 300px)' /* 1.73" */

function useTheme() {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return 'dark'
    const existing = document.documentElement.getAttribute('data-theme')
    if (existing === 'light' || existing === 'dark') return existing
    try {
      const saved = localStorage.getItem(THEME_STORAGE_KEY)
      if (saved === 'light' || saved === 'dark') return saved
      if (window.matchMedia?.('(prefers-color-scheme: light)').matches) return 'light'
    } catch (_) {}
    return 'dark'
  })
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch (_) {}
  }, [theme])
  const setTheme = useCallback((next) => {
    setThemeState(prev => (next === 'light' ? 'light' : 'dark'))
  }, [])
  const toggleTheme = useCallback(() => {
    setThemeState(prev => (prev === 'dark' ? 'light' : 'dark'))
  }, [])
  return [theme, setTheme, toggleTheme]
}

function useWatchView() {
  const [watchView, setWatchView] = useState(false)
  useEffect(() => {
    setWatchView(isWearOSDevice())
  }, [])
  return watchView
}

function useTinyWatch(isWatchView) {
  const [tiny, setTiny] = useState(false)
  useEffect(() => {
    if (!isWatchView) {
      setTiny(false)
      return
    }
    const update = () => setTiny(isTinyWatchScreen())
    update()
    try {
      if (typeof window !== 'undefined' && window.matchMedia) {
        const m = window.matchMedia(TINY_WATCH_MEDIA)
        m.addEventListener('change', update)
        return () => m.removeEventListener('change', update)
      }
    } catch (_) {}
  }, [isWatchView])
  return tiny
}

function AppContent() {
  const { state, loadState } = useAppState()
  const [theme, , toggleTheme] = useTheme()
  const [dayIndex, setDayIndex] = useState(0)
  const [goalsOpen, setGoalsOpen] = useState(false)
  const [deviceDetectOpen, setDeviceDetectOpen] = useState(false)
  const [debugMenuOpen, setDebugMenuOpen] = useState(false)
  const debugMenuWrapRef = useRef(null)
  const isWatchView = useWatchView()

  useEffect(() => {
    if (!debugMenuOpen) return
    const close = e => {
      if (debugMenuWrapRef.current?.contains(e.target)) return
      setDebugMenuOpen(false)
    }
    const onKey = e => {
      if (e.key === 'Escape') setDebugMenuOpen(false)
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [debugMenuOpen])
  const isTinyWatch = useTinyWatch(isWatchView)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) loadState(JSON.parse(raw))
    } catch (_) {}
  }, [loadState])

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch (_) {}
  }, [state])

  const day = WEEKDAYS[dayIndex]
  const hasGoals = Object.values(state.goals).some(Boolean)
  const goalPreview = hasGoals
    ? (state.goals.professionalWeekly || state.goals.privateWeekly || state.goals.professionalMonthly || state.goals.privateMonthly || Object.values(state.goals).find(Boolean) || '').slice(0, 40)
    : ''

  return (
    <div className={`app-wrap ${isWatchView ? 'watch-view' : ''}`}>
      {deviceDetectOpen && (
        <WelcomePopup
          deviceName={getDeviceDisplayName()}
          icon={getDeviceIcon()}
          onClose={() => setDeviceDetectOpen(false)}
          debugInfo={getDeviceDebugInfo()}
        />
      )}
      <header className="app-header">
        <div className="brand" onClick={() => setGoalsOpen(o => !o)}>
          <img src={theme === 'dark' ? '/grim/logo-white.webp' : '/grim/logo-black-bg.webp'} alt="" className="brand-logo" aria-hidden />
          <span className="brand-text">GRIM</span>
          <span className="brand-tagline">
            {goalPreview ? `${goalPreview}${goalPreview.length >= 40 ? '…' : ''}` : 'Honor will come. Tap to set goals.'}
          </span>
        </div>
        {!isWatchView && (
          <div className="header-actions">
            <div className="debug-menu-wrap" ref={debugMenuWrapRef}>
              <button
                type="button"
                className="debug-btn"
                onClick={() => setDebugMenuOpen(open => !open)}
                title="Debug"
                aria-label="Debug"
                aria-expanded={debugMenuOpen}
                aria-haspopup="menu"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="m10 4 1.5 1.5M14 4l-1.5 1.5" />
                  <path d="M12 4v3" />
                  <path d="M8 11a4 4 0 0 1 8 0v5a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2z" />
                  <path d="m5 12-2 2 2 2" />
                  <path d="m19 12 2 2-2 2" />
                  <path d="m6 16-2 2" />
                  <path d="m18 16 2 2" />
                </svg>
              </button>
              {debugMenuOpen && (
                <div className="debug-dropdown" role="menu">
                  <button
                    type="button"
                    onClick={() => {
                      setDebugMenuOpen(false)
                      setDeviceDetectOpen(true)
                    }}
                  >
                    Detect device
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              className="theme-toggle"
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? '☀' : '☽'}
            </button>
          </div>
        )}
        <GoalsInSight open={goalsOpen} onClose={() => setGoalsOpen(false)} />
      </header>

      <nav className="day-tabs">
        {WEEKDAYS.map((d, i) => (
          <button
            key={d}
            className={`day-tab ${i === dayIndex ? 'active' : ''}`}
            onClick={() => setDayIndex(i)}
          >
            {d}
          </button>
        ))}
      </nav>

      <main className="main">
        <DayView day={day} isWatchView={isWatchView} isTinyWatch={isTinyWatch} />
      </main>
      {!isWatchView && (
        <footer className="app-disclaimer">
          Not affiliated or associated with Ring of Grim, Grim Circle, or The Grim Foundation. This is a non-commercial project for personal use.
        </footer>
      )}
    </div>
  )
}

export default function App() {
  return (
    <AppStateProvider>
      <AppContent />
    </AppStateProvider>
  )
}
